/**
 * Native Sign in with Apple (iOS) and Google (iOS, Android) through
 * `@capgo/capacitor-social-login` (v8), loaded from the native entry chunk
 * (NativeLoaders.socialLogin). The web popups cannot run in the WebView (Google
 * refuses embedded browsers), so native builds use the system sheets instead.
 *
 * Both return an OIDC id token for POST /v1/auth/link/:provider (IdTokenLink), like the
 * web providers. Its audience differs from the web one: Apple uses the app's bundle id,
 * Google the web client id on Android (Credential Manager's server client id) and the
 * iOS client id on iOS, so the API's IDP_<P>_AUDIENCE must accept those too.
 *
 * Replay protection (docs/ops/native-apps.md, Nonce): every sign-in first fetches a
 * single-use nonce (GET /v1/auth/nonce) and asks the sheet for a token bound to it. Google
 * (GoogleSignIn on iOS, Credential Manager on Android) puts the nonce into the token as is.
 * Apple copies ASAuthorizationAppleIDRequest.nonce into the token's claim unchanged, and the
 * plugin passes ours straight through, so Apple gets the SHA-256 hex of the nonce (Apple's and
 * Firebase's convention: the raw value never travels through Apple). The raw nonce goes to the
 * API next to the token either way; the API accepts the claim in both forms and spends it.
 *
 * Token revocation (App Store Review Guideline 5.1.1(v)): the plugin runs with
 * `useProperTokenExchange`, so Apple's sheet also hands over the authorization code of the sign-in
 * (instead of passing it off as an access token), and it goes to the API with the id token. The API
 * exchanges it for the refresh token it revokes on unlink and account deletion. An empty
 * `redirectUrl` keeps the plugin from exchanging the single-use code itself.
 */
import type { Loader } from '../lazy.js';
import { sha256Hex } from './nonce.js';
import { SignInCancelled, type IdentityProvider, type IdentityToken } from './types.js';

export { fetchAuthNonce, sha256Hex } from './nonce.js';

type GoogleResult = { responseType: 'online'; idToken: string | null } | { responseType: 'offline'; serverAuthCode: string };

/** The part of SocialLoginPlugin used here, as in its definitions.d.ts. */
export interface SocialLoginPlugin {
  initialize(o: {
    apple?: { clientId?: string; redirectUrl?: string; useProperTokenExchange?: boolean };
    google?: { iOSClientId?: string; iOSServerClientId?: string; webClientId?: string; mode?: 'online' | 'offline' };
  }): Promise<void>;
  login(o: { provider: 'apple'; options: { scopes?: string[]; nonce?: string } }): Promise<{ provider: 'apple'; result: { idToken: string | null; authorizationCode?: string } }>;
  login(o: { provider: 'google'; options: { scopes?: string[]; nonce?: string } }): Promise<{ provider: 'google'; result: GoogleResult }>;
  logout(o: { provider: 'apple' | 'google' }): Promise<void>;
}
export interface SocialLoginModule {
  SocialLogin: SocialLoginPlugin;
}

export interface NativeSignInConfig {
  /** Sign in with Apple through the iOS system sheet (the App ID needs the capability); iOS only */
  apple?: { scopes?: string[] };
  google?: {
    /** OAuth web client id (…apps.googleusercontent.com): the audience of Android id tokens */
    webClientId: string;
    /** OAuth iOS client id, required on iOS; its reversed form is a URL scheme in Info.plist */
    iosClientId?: string;
  };
}

export interface NativeSocialLoginOptions extends NativeSignInConfig {
  platform: 'ios' | 'android';
  load: Loader<SocialLoginModule>;
  /** a fresh single-use nonce for each sign-in (fetchAuthNonce); null signs in without one */
  nonce?: () => Promise<string | null>;
}

/** One plugin initialization shared by the Apple and Google providers. */
export class NativeSocialLogin {
  private ready: Promise<{ SocialLogin: SocialLoginPlugin }> | null = null;

  constructor(readonly o: NativeSocialLoginOptions) {}

  /**
   * The initialized plugin inside an object, never on its own: resolving a promise with
   * Capacitor's plugin proxy looks up its `then`, which the proxy turns into a native call that
   * iOS and Android reject as UNIMPLEMENTED, and the promise never settles (sign-in hung).
   */
  plugin(): Promise<{ SocialLogin: SocialLoginPlugin }> {
    this.ready ??= this.o
      .load()
      .then(async ({ SocialLogin }) => {
        const { apple, google, platform } = this.o;
        await SocialLogin.initialize({
          // iOS signs in natively: an empty redirect URL keeps the plugin off its web flow (and off the code, which the
          // API exchanges); useProperTokenExchange returns that code as `authorizationCode`
          ...(apple && platform === 'ios' ? { apple: { redirectUrl: '', useProperTokenExchange: true } } : {}),
          ...(google
            ? { google: { webClientId: google.webClientId, mode: 'online' as const, ...(google.iosClientId ? { iOSClientId: google.iosClientId, iOSServerClientId: google.webClientId } : {}) } }
            : {}),
        });
        return { SocialLogin };
      })
      .catch((e: unknown) => {
        this.ready = null;
        throw e;
      });
    return this.ready;
  }
}

/** What a sheet returned: the id token, and for Apple the authorization code of the same sign-in. */
interface SheetResult {
  idToken: string | null;
  authorizationCode?: string;
}

const isCancel = (e: unknown): boolean => {
  const { code, message } = (e ?? {}) as { code?: unknown; message?: unknown };
  return code === 'USER_CANCELLED' || /cancel/i.test(String(message ?? ''));
};

export class NativeIdentityProvider implements IdentityProvider {
  constructor(
    readonly provider: 'apple' | 'google',
    private readonly session: NativeSocialLogin,
  ) {}

  async signIn(): Promise<IdentityToken> {
    const { SocialLogin: plugin } = await this.session.plugin();
    // a nonce is single use and lives 10 minutes: one per sheet, fetched right before it opens
    const nonce = (await this.session.o.nonce?.()) ?? null;
    const { idToken, authorizationCode } = await (this.provider === 'apple' ? this.apple(plugin, nonce) : this.google(plugin, nonce)).catch((e: unknown) => {
      if (isCancel(e)) throw new SignInCancelled(this.provider);
      throw e;
    });
    if (!idToken) throw new Error(`${this.provider} sign-in returned no id token`);
    return { provider: this.provider, idToken, ...(nonce ? { nonce } : {}), ...(authorizationCode ? { authorizationCode } : {}) };
  }

  /** Forget the platform session, so the next sign-in shows the account picker again. */
  async signOut(): Promise<void> {
    const { SocialLogin } = await this.session.plugin();
    await SocialLogin.logout({ provider: this.provider });
  }

  private async apple(plugin: SocialLoginPlugin, nonce: string | null): Promise<SheetResult> {
    const hashed = nonce === null ? null : await sha256Hex(nonce);
    const { result } = await plugin.login({ provider: 'apple', options: { scopes: this.session.o.apple?.scopes ?? ['email'], ...(hashed ? { nonce: hashed } : {}) } });
    return { idToken: result.idToken, ...(result.authorizationCode ? { authorizationCode: result.authorizationCode } : {}) };
  }

  /** No scopes: extra Google scopes need a MainActivity change on Android, and the id token carries the email already. */
  private async google(plugin: SocialLoginPlugin, nonce: string | null): Promise<SheetResult> {
    const { result } = await plugin.login({ provider: 'google', options: nonce ? { nonce } : {} });
    return { idToken: result.responseType === 'online' ? result.idToken : null };
  }
}
